"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useLoggingDecorator = exports.LoggingDecorator = exports.LoggableEvent = void 0;
exports.useLoggingDisabledByDefault = useLoggingDisabledByDefault;
exports.useLoggingEnabledByDefault = useLoggingEnabledByDefault;
var _web = require("@react-hookz/web");
var _loggingStateService = require("@smartsheet/logging-state-service");
var _react = require("react");
class LoggableEvent {
  content;
  _isLoggedByDefault;
  _shouldLog;
  constructor(logByDefault, content) {
    this.content = content;
    this._shouldLog = logByDefault;
    this._isLoggedByDefault = logByDefault;
  }
  isLoggedByDefault() {
    return this._isLoggedByDefault;
  }
  preventLogging() {
    this._shouldLog = false;
  }
  enableLogging() {
    this._shouldLog = true;
  }
  shouldLog() {
    return this._shouldLog;
  }
}
exports.LoggableEvent = LoggableEvent;
const isSyntheticEvent = arg => {
  return arg !== null && typeof arg === "object" && "nativeEvent" in arg && "persist" in arg;
};
class LoggingDecorator {
  logger;
  defaultContent;
  constructor(logger, defaultContent) {
    this.logger = logger;
    this.defaultContent = defaultContent;
  }
  enabledByDefault = (content, fn) => this.partialCallWithLogging(fn, new LoggableEvent(true, {
    ...this.defaultContent,
    ...content
  }));
  disabledByDefault = (content, fn) => this.partialCallWithLogging(fn, new LoggableEvent(false, {
    ...this.defaultContent,
    ...content
  }));
  partialCallWithLogging(fn, log) {
    var _this = this;
    return async function () {
      for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }
      // In the majority case, an event is passed somewhere here.
      // Because this is all async, we should persist those events so they don't
      // get recycled back to the SyntheticEvent pool.
      args.forEach(arg => {
        if (isSyntheticEvent(arg)) {
          arg.persist();
        }
      });
      await fn(...args, log);
      if (log.shouldLog()) {
        const {
          clientId,
          controlType,
          actionId
        } = log.content;
        if (clientId) {
          _this.logger?.logClientEvent(controlType, clientId, actionId);
        }
      }
    };
  }
}

// Adapted from http://adripofjavascript.com/blog/drips/object-equality-in-javascript.html
exports.LoggingDecorator = LoggingDecorator;
function objectsShallowEqual(a, b) {
  // Short-circuit in the case the object is the same.
  if (a === b) {
    return true;
  }
  // Create arrays of property names
  const aProps = Object.getOwnPropertyNames(a);
  const bProps = Object.getOwnPropertyNames(b);

  // If number of properties is different,
  // objects are not equivalent
  if (aProps.length != bProps.length) {
    return false;
  }
  for (let i = 0; i < aProps.length; i++) {
    const propName = aProps[i];

    // If values of same property are not equal,
    // objects are not equivalent
    if (a[propName] !== b[propName]) {
      return false;
    }
  }

  // If we made it this far, objects
  // are considered equivalent
  return true;
}

/**
 * Hook access to a LoggingDecorator.
 * Some effort is expended here to ensure we only return a new LoggingDecorator if the
 * `defaultContent` actually changes its contents to prevent re-renders.
 * @param defaultContent
 */
const useLoggingDecorator = defaultContent => {
  const logger = (0, _loggingStateService.useLogger)();
  const [loggingDecorator, setLoggingDecorator] = (0, _react.useState)(new LoggingDecorator(logger, defaultContent));
  const previousDefaultContent = (0, _web.usePrevious)(defaultContent);
  (0, _react.useEffect)(() => {
    if (previousDefaultContent && !objectsShallowEqual(previousDefaultContent, defaultContent)) {
      setLoggingDecorator(new LoggingDecorator(logger, defaultContent));
    }
  }, [defaultContent, logger, previousDefaultContent]);
  return loggingDecorator;
};
exports.useLoggingDecorator = useLoggingDecorator;
function useLoggingEnabledByDefault(withLogging, newContent, handler) {
  const [content, setContent] = (0, _react.useState)(newContent);
  const previousContent = (0, _web.usePrevious)(content);
  (0, _react.useEffect)(() => {
    if (previousContent && !objectsShallowEqual(previousContent, newContent)) {
      setContent(newContent);
    }
  }, [previousContent, newContent]);
  return (0, _react.useMemo)(() => handler ? withLogging.enabledByDefault(content, handler) : () => undefined, [withLogging, content, handler]);
}
function useLoggingDisabledByDefault(withLogging, newContent, handler) {
  const [content, setContent] = (0, _react.useState)(newContent);
  const previousContent = (0, _web.usePrevious)(content);
  (0, _react.useEffect)(() => {
    if (previousContent && !objectsShallowEqual(previousContent, newContent)) {
      setContent(newContent);
    }
  }, [previousContent, newContent]);
  return (0, _react.useMemo)(() => handler !== undefined ? withLogging.disabledByDefault(content, handler) : () => undefined, [withLogging, content, handler]);
}
//# sourceMappingURL=logging.js.map